import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@heroui/react";

interface CloneWorkflowModalProps {
  isOpen: boolean;
  workflowId: string;
  workflowName: string;
  onClose: () => void;
  onConfirm: (id: string) => Promise<void>;
}

export const CloneWorkflowModal: React.FC<CloneWorkflowModalProps> = ({
  isOpen,
  workflowId,
  workflowName,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await onConfirm(workflowId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clone workflow");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader className="text-brand-primary dark:!text-white font-bold">
          {t("Clone Workflow")}
        </ModalHeader>
        <ModalBody>
          <p>
            {t("You are about to clone the workflow")} <strong>"{workflowName}"</strong>.
          </p>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-default-600">{t("The cloned workflow will:")}</p>
            <ul className="list-disc list-inside text-sm text-default-600 space-y-1 ml-2">
              <li>{t("Be an exact copy with all settings and configurations")}</li>
              <li>{t("Have '_v2' appended to the workflow ID")}</li>
              <li>
                {t("Be named")} "{workflowName} (Copy)"
              </li>
              <li>{t("Be owned by you")}</li>
            </ul>
          </div>
          <p className="text-sm text-default-500 mt-4">
            {t("You can modify the cloned workflow independently from the original.")}
          </p>
          {error && (
            <p className="text-sm text-danger mt-4" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button isDisabled={isLoading} variant="flat" onPress={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            className="btn-brand-primary"
            isDisabled={isLoading}
            isLoading={isLoading}
            onPress={handleConfirm}
          >
            {t("Clone Workflow")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
